import { computed, ref, onMounted, watch } from 'vue'

import type { Ref, ComputedRef } from 'vue'
import type { NativeSelectOption, NativeSelectProps } from '../types/interfaces'

const PLACEHOLDER_VALUE = 'NATIVE_SELECT_PLACEHOLDER'

interface NativeSelectOptions {
  props: NativeSelectProps
  onChange: (option: NativeSelectOption) => void
}

interface NativeSelectReturn {
  refOptions: Ref<NativeSelectOption[]>
  refCurrent: Ref<string | number | null>
  colorText: ComputedRef<string>
  isPlaceholder: ComputedRef<boolean>
  placeholderValue: string
  handleChange: () => void
}

export const useNativeSelect = ({ props, onChange }: NativeSelectOptions): NativeSelectReturn => {
  const refOptions = ref<NativeSelectOption[]>([...props.options])
  const refCurrent = ref<string | number | null | typeof PLACEHOLDER_VALUE>(
    props.value ?? (props.placeholderEnabled ? PLACEHOLDER_VALUE : props.options[0]?.value ?? ''),
  )

  const setCurrentOption = (): void => {
    refOptions.value = refOptions.value.map(option => ({
      ...option,
      selected: option.value === refCurrent.value,
    }))
  }

  watch(() => props.options, (options) => {
    refOptions.value = [...options]

    // Reconcile the current value: when it is gone from the new options (or
    // options loaded after an initially empty list), fall back to the bound
    // value / placeholder / first option instead of keeping a stale selection.
    const exists = options.some(option => option.value === refCurrent.value)
    if (!exists) {
      refCurrent.value = props.value ?? (props.placeholderEnabled ? PLACEHOLDER_VALUE : options[0]?.value ?? '')
    }

    setCurrentOption()
  })

  watch(() => props.value, (value) => {
    refCurrent.value = value ?? (props.placeholderEnabled ? PLACEHOLDER_VALUE : props.options[0]?.value ?? '')
    setCurrentOption()
  })

  const getCurrentOption = computed(() => refOptions.value.find(option => option.value === refCurrent.value))
  const colorText = computed(() => getCurrentOption.value?.colorText ? `text-${getCurrentOption.value.colorText}` : '')
  const isPlaceholder = computed(() => (props.placeholderEnabled && refCurrent.value === PLACEHOLDER_VALUE) || false)

  const handleChange = (): void => {
    setCurrentOption()
    const currentOption = getCurrentOption.value
    if (currentOption) {
      onChange(currentOption)
    }
  }

  onMounted(() => {
    if (props.changeOnMount) {
      handleChange()
    }
  })

  return {
    refOptions,
    refCurrent,
    colorText,
    isPlaceholder,
    placeholderValue: PLACEHOLDER_VALUE,
    handleChange,
  }
}
